import type {
  GuestCommunicationChannel,
  GuestCommunicationMessageKey,
  PropertyCommunicationCatalogResponse,
} from '@/shared/api';
import { hasOverride } from './catalogModel';
import { changes, LANGUAGE_LABEL, type Change, type Issue } from './editorModel';
import { CHANNEL_LABEL } from './messagePresentation';

/** Die Gastreise in fester Zeitfolge — unabhängig davon, in welcher Reihenfolge der Server liefert. */
const JOURNEY: { key: GuestCommunicationMessageKey; when: string; title: string }[] = [
  { key: 'Welcome', when: 'Nach Buchung', title: 'Willkommen' },
  { key: 'Reminder5d', when: 'T − 5 Tage', title: 'Fünf Tage vorher' },
  { key: 'Reminder1d', when: 'T − 1 Tag', title: 'Vortag' },
  { key: 'ArrivalDayNudge', when: 'Anreisetag', title: 'Anreisetag' },
  { key: 'PostStay', when: 'Nach Abreise', title: 'Danke' },
];

/** Vortag und Anreisetag teilen einen Schalter: es geht höchstens eine der beiden raus. */
export const LAST_REMINDER_LABEL = 'Letzte Erinnerung';
export const isLastReminder = (key: GuestCommunicationMessageKey) =>
  key === 'Reminder1d' || key === 'ArrivalDayNudge';

export interface Occasion {
  key: GuestCommunicationMessageKey;
  when: string;
  title: string;
  required: boolean;
  enabled: boolean;
  /** Eigene Texte des Objekts im Entwurf. */
  customized: boolean;
  /** Der Entwurf weicht bei diesem Anlass vom gespeicherten Stand ab. */
  unsaved: boolean;
  /** Felder, die der Server so ablehnen würde. */
  issues: number;
  channels: GuestCommunicationChannel[];
}

export function journeyOccasions(
  draft: PropertyCommunicationCatalogResponse,
  baseline: PropertyCommunicationCatalogResponse,
  issues: Issue[] = [],
): Occasion[] {
  const pending = changes(draft, baseline);
  return JOURNEY.flatMap((step) => {
    const message = draft.messages.find((item) => item.key === step.key);
    if (!message) return [];
    return [
      {
        ...step,
        required: message.isRequired,
        enabled: message.isEnabled,
        customized: message.channels.some(hasOverride),
        unsaved: pending.some((change) => change.key === step.key),
        issues: issues.filter((issue) => issue.key === step.key).length,
        channels: message.channels.map((channel) => channel.channel),
      },
    ];
  });
}

/** Der Name, unter dem ein Schalter in Rückmeldungen erscheint. */
export function switchLabel(occasions: Occasion[], key: GuestCommunicationMessageKey) {
  if (isLastReminder(key)) return LAST_REMINDER_LABEL;
  return occasions.find((occasion) => occasion.key === key)?.title ?? key;
}

/** Der kurze Name eines Anlasses, wie in der Gastreise. */
export const journeyTitle = (key: GuestCommunicationMessageKey) =>
  JOURNEY.find((step) => step.key === key)?.title ?? key;

/** „Willkommen · E-Mail · Deutsch · Betreff" für eine Änderung, sonst die betroffenen Anlässe. */
export function changeSummary(pending: Change[]) {
  if (pending.length === 1) {
    const [only] = pending;
    return [
      journeyTitle(only!.key),
      CHANNEL_LABEL[only!.channel],
      LANGUAGE_LABEL[only!.lang],
      only!.field.label,
    ].join(' · ');
  }
  return [...new Set(pending.map((change) => journeyTitle(change.key)))].join(', ');
}
